<h2 mat-dialog-title>Agent Data Model</h2>
<div mat-dialog-content>
  <p *ngIf="isNew" class="text-muted" style="margin-top: -8px">
    Once this Agent Data Model is generated, SQL and AI-agent connection details will be available from its
    "Connection Info" icon.
  </p>
  <mat-tab-group>
    <mat-tab label="General">
      <div class="row" style="margin-top: 10px">
        <div class="col-md-12">
          <mat-form-field class="full-width">
            <mat-label>Name</mat-label>
            <input matInput [(ngModel)]="result.name" name="name" />
          </mat-form-field>
        </div>
      </div>
      <div class="row" *ngIf="data.subscriptions && data.subscriptions.length > 1">
        <div class="col-md-12">
          <mat-form-field class="full-width">
            <mat-label>Subscription</mat-label>
            <mat-select [(value)]="result.subscriptionId" (selectionChange)="onSubscriptionChange()" [disabled]="!isNew">
              <mat-option *ngFor="let sub of data.subscriptions" [value]="sub.subscriptionId">
                {{ sub.subscriptionName || sub.organizationName }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </div>
      <div class="row" *ngIf="loadingGroups">
        <div class="col-md-12"><i class="fas fa-spinner fa-pulse"></i> Loading Agent Data Model Groups...</div>
      </div>
      <div class="row" *ngIf="!loadingGroups && availableGroups.length > 1">
        <div class="col-md-12">
          <mat-form-field class="full-width">
            <mat-label>Agent Data Model Group</mat-label>
            <mat-select [(value)]="result.admGroupId" (selectionChange)="onGroupChange()" [disabled]="!isNew">
              <mat-option *ngFor="let g of availableGroups" [value]="g.admGroupId">{{ g.name }}</mat-option>
            </mat-select>
          </mat-form-field>
          <p class="text-muted" style="margin-top: -10px">
            Tables are limited to this Group only - Groups are never combined into one Agent Data Model.
          </p>
        </div>
      </div>
      <div class="row" *ngIf="!loadingGroups && availableGroups.length === 1">
        <div class="col-md-12 text-muted">Agent Data Model Group: <b>{{ availableGroups[0].name }}</b></div>
      </div>
      <div class="row" *ngIf="!loadingGroups && availableGroups.length === 0">
        <div class="col-md-12 text-muted">This subscription is not entitled to any Agent Data Model Groups.</div>
      </div>
      <div class="row" *ngIf="data.isAdmin && !data.layout.admLayoutId">
        <div class="col-md-12">
          <mat-checkbox [(ngModel)]="result.isSubscriptionAdm" name="isSubscriptionAdm">Create Subscription ADM</mat-checkbox>
        </div>
      </div>
    </mat-tab>

    <mat-tab label="Columns">
      <div style="margin-top: 10px" *ngIf="loadingCatalog"><i class="fas fa-spinner fa-pulse"></i> Loading available tables...</div>
      <p class="text-muted" *ngIf="!loadingCatalog && catalog.length === 0">
        {{ result.admGroupId ? "No tables are available for the selected Group." : "Select an Agent Data Model Group on the General tab first." }}
      </p>

      <mat-form-field class="full-width" style="margin-top: 10px" *ngIf="catalog.length > 0">
        <mat-label>Table</mat-label>
        <mat-select [(value)]="selectedTableId">
          <mat-option *ngFor="let table of catalog" [value]="table.admTableId">
            {{ table.displayName || table.tableName }} ({{ selectedCount(table) }}/{{ columns(table).length }} selected)
          </mat-option>
        </mat-select>
      </mat-form-field>

      <ng-container *ngIf="selectedTable">
        <p class="text-muted" *ngIf="selectedTable.description">{{ selectedTable.description }}</p>
        <mat-checkbox
          class="select-all-row"
          [checked]="isTableSelected(selectedTable)"
          [indeterminate]="isTableIndeterminate(selectedTable)"
          (change)="toggleTable(selectedTable, $event.checked)">
          <b>Select All</b>
        </mat-checkbox>

        <div *ngFor="let group of columnGroups(selectedTable); trackBy: trackByFieldGroupId" style="margin-top: 12px">
          <mat-checkbox
            [checked]="isGroupSelected(group)"
            [indeterminate]="isGroupIndeterminate(group)"
            (change)="toggleGroup(group, $event.checked)">
            <span class="columns-group-header">{{ group.name }}</span>
          </mat-checkbox>
          <div class="columns-group-container" style="margin-left: 20px">
            <div *ngFor="let c of group.columns; trackBy: trackByColumnId" class="tight-list-item">
              <mat-checkbox [checked]="isSelected(c)" (change)="toggleColumn(c, $event.checked)" [matTooltip]="c.displayName || c.outputName" matTooltipShowDelay="500">
                <span class="item-title">{{ c.displayName || c.outputName }}</span>
              </mat-checkbox>
              <span class="item-description" *ngIf="c.description" [matTooltip]="c.description" matTooltipShowDelay="500">- {{ c.description }}</span>
              <span class="item-description" *ngIf="c.isHighCardinality">(high cardinality)</span>
            </div>
          </div>
        </div>
      </ng-container>
    </mat-tab>

    <mat-tab label="Filters">
      <mat-form-field style="width: 100%">
        <mat-label>Filtering Source</mat-label>
        <mat-select [(value)]="filteringSource">
          <mat-option [value]="-1">Direct Filter</mat-option>
          <ng-container *ngFor="let preFilter of data.preFilters">
            <mat-option [value]="preFilter.preFilterId">PreFilter: {{ preFilter.name }}</mat-option>
          </ng-container>
        </mat-select>
      </mat-form-field>
      <div class="row">
        <div class="col-md-6" *ngIf="filteringSource === -1">
          <button mat-raised-button class="piButton full-width" (click)="editDirectFilter()">Edit Direct Filter</button>
        </div>
        <div class="col-md-6" *ngIf="filteringSource === -1">
          <input #fileInput type="file" (change)="handleFileInput($event.target.files)" style="display: none" />
          <button mat-raised-button class="piButton full-width" (click)="fileInput.click()">Upload Filter Template</button>
        </div>
        <div class="col-md-6" *ngIf="filteringSource !== -1">
          <button mat-raised-button class="piButton full-width" (click)="copyPreFilterToDirect()">Copy PreFilter to Direct Filter</button>
        </div>
      </div>
      <p *ngIf="uploadStatusMessage" style="margin-top: 10px"><b>{{ uploadStatusMessage }}</b></p>
      <mat-error *ngIf="uploadErrorMessage" style="margin-top: 10px">{{ uploadErrorMessage }}</mat-error>
      <p class="text-muted" style="margin-top: 10px">Some filtering is required so an Agent Data Model never exposes a whole source table.</p>
    </mat-tab>
  </mat-tab-group>

  <ng-container *ngIf="errorMessage">
    <mat-error>{{ errorMessage }}</mat-error>
  </ng-container>
</div>
<div mat-dialog-actions>
  <button mat-raised-button class="piButton" (click)="onSave()">Save</button>
  <button mat-raised-button class="piButton" (click)="onCancelClick()">Cancel</button>
</div>
